import type { DateTime } from "luxon";
import type { NurseFullRole, OpenShiftRequest, ShiftRequest } from "@/utils/models";
import { isActivePostedRequest } from "@/utils/openShiftMatching";

export interface PostedMatch {
	requestId: string;
	providedAmountAfterMatching: number;
	/** Posted shift start in minutes-from-midnight (uses request's local clock time). */
	postedStartMin: number;
	/** Posted shift end in minutes-from-midnight (uses request's local clock time). */
	postedEndMin: number;
}

export type TryMatchFn = (
	locationName: string,
	dayDate: DateTime,
	startMin: number,
	endMin: number,
	roles: NurseFullRole[],
	requiredAmountInLocation: number,
	// Required (not optional) so every caller intentionally opts in or out of job-aware matching:
	// a job id constrains matching to that job; `null` means "no job constraint" (role/role-group
	// sections, or the single-day Day view). Making it required prevents a new caller silently
	// skipping the job dimension.
	sectionJobId: string | null,
) => PostedMatch | null;

// Minutes in a day — used to unwrap overnight intervals before comparison.
const MINUTES_PER_DAY = 1440;
// Guards float-precision misses when deciding if remaining budget can still cover a fractional unit.
const BUDGET_EPS = 1e-6;
// A candidate must overlap at least this fraction of the SHORTER of the two intervals
// (posted shift vs. the passed slot interval — a group window or an individual config slot)
// to count as a match. See the threshold rationale in `tryMatch`.
const MIN_OVERLAP_FRACTION = 0.5;
// Float tolerance for the overlap-threshold comparison so exact-boundary matches (overlap == threshold) aren't lost to rounding.
const OVERLAP_EPS = 1e-6;

/**
 * Creates a matcher from a list of staff requests.
 * The returned `tryMatch` mutates an internal budget, so it must only be called
 * once per gap slot inside a single computation pass (e.g. inside useMemo).
 *
 * Budget is fractional and credited by overlap: each match consumes
 * `overlapMinutes / postedDurationMinutes` of the request's budget (per required slot).
 * This lets a single posted shift mark "Posted" across multiple sections whose combined
 * overlap equals its own duration — e.g. a 6a–6p (12h) posted shift with needed_nurses=1
 * marks both a 6a–2p section (8h overlap = 8/12) and an overlapping 2p–10p section
 * (4h overlap = 4/12), summing to 1.0 budget.
 *
 * Job-aware filtering: when a `sectionJobId` is passed (Jobs view), a candidate must belong to the
 * same job — `req.job_id === sectionJobId`. A request with no `job_id` falls back to role matching so
 * legacy/role-only requests still work, and role/role-group sections pass no `sectionJobId` so they
 * stay unconstrained. Without this, every job section is offered every request and a request can
 * render under the wrong job.
 *
 * Location-wildcard filtering: gap cells are location-specific ("Cottage 4"), but a department-wide
 * open shift is stored with no location (`location` null/empty). Mirroring the job fallback, a
 * location-less request matches ANY location cell, so a dept-wide "Posted" badge surfaces on the
 * specific-location gaps it can fill. Within each pass candidates are ordered located-first (exact
 * `req.location === locationName` before wildcard), so a request that names this location claims its
 * own cell before a dept-wide request consumes that cell's budget.
 *
 * Per-cell ordering — strict-then-loose: candidates are tried in two ordered passes.
 * Strict-eligible requests (`isStrict(req.id) === true`) get first dibs and must clear the overlap
 * threshold gate below. Only if no strict request claims the cell do loose-eligible requests
 * (`isStrict(req.id) === false`) get a turn, with NO threshold gate (any positive overlap qualifies).
 * The caller decides eligibility globally (a request is loose only if it found no strict home
 * anywhere), so a request whose time has no open gap can still surface via the loose fallback rather
 * than vanishing. With the default `isStrict` (all strict, no `onMatched`) there is no loose pass at
 * all — every candidate must clear the threshold gate. Callers that don't run the two-pass probe
 * (e.g. the single-day Day view) use this default and therefore get strict-only matching, WITHOUT the
 * fallback. That is intended: the Day view resolves the correct location/role/job row before calling
 * `tryMatch` and its slots are discrete real times, so a sub-threshold sliver there is a genuine
 * non-match, not a group-window artifact to rescue.
 *
 * Match gate (strict pass): a candidate only matches a slot when their overlap is at least
 * `MIN_OVERLAP_FRACTION` (0.5) of the SHORTER of the two intervals (posted shift vs. the passed slot —
 * group window or config slot). 0.5 is the exact, only-safe coefficient. Groups are iterated ascending
 * by start time and the first match wins, so without the gate a Night shift (6p–6:30a) that only clips
 * a 30-min boundary sliver of an earlier Day group (6a–6:30p) would be wrongly claimed by Day.
 * Half-overlap is the unique cutoff: the widest legitimate spanning match (6a–6p posted vs. an abutting
 * 2p–10p group) sits at exactly half the shorter duration, so a higher coefficient would regress that
 * spanning case, while a lower one would reopen the sliver bug. The comparison is `>=` (with
 * `OVERLAP_EPS` float tolerance) precisely because that spanning case lands ON the boundary and must
 * still match.
 *
 * Exact-window reservation: cells are visited in group order, so without it a 4a–4p post is claimed by
 * an earlier 2:30a–10:30a group it merely overlaps (6.5h, over the gate) before the 4a–4p group it was
 * posted for gets a turn. A caller that first runs an `onlyExactWindow` pass learns how much of each
 * request's budget cells with ITS OWN window can take (`reservedForExact`); a later pass then keeps that
 * much back from every other cell, so it is left for the exact cells. Budget beyond the reservation
 * still matches by overlap as before.
 */
export function createPostedRequestMatcher(
	staffRequests: ShiftRequest[] | null,
	// Defaults = strict-only, single pass, no exact-window reservation: every request must clear the overlap
	// threshold and no matches are reported. Single-arg callers (useDayViewGapsState) and the tests rely on
	// this — see the "no loose pass" note in the docstring for why strict-only is correct for the Day view.
	options?: {
		isStrict?: (reqId: string) => boolean;
		/** `amount` is the budget the match consumed — whole people on an exact-window cell. */
		onMatched?: (reqId: string, amount: number) => void;
		/** Match only cells whose window is the request's own, to the minute. */
		onlyExactWindow?: boolean;
		/** Budget per request that only cells with the request's own window may consume. */
		reservedForExact?: ReadonlyMap<string, number>;
	},
): { tryMatch: TryMatchFn } {
	const isStrict = options?.isStrict ?? (() => true);
	const onMatched = options?.onMatched ?? (() => {});
	const onlyExactWindow = options?.onlyExactWindow === true;
	// DEFAULT: no reservation — the Day view and the reservation pass itself let every cell use the whole budget.
	const reserved = new Map(options?.reservedForExact ?? []);

	const activeRequests = (staffRequests ?? []).filter(isActivePostedRequest);

	const budgets = new Map<string, number>();
	for (const req of activeRequests) {
		const accepted = req.message.request_data?.filter((d) => d.status === "approved").length ?? 0;
		budgets.set(req.id, Math.max(0, req.needed_nurses - accepted));
	}

	function tryMatch(
		locationName: string,
		dayDate: DateTime,
		startMin: number,
		endMin: number,
		roles: NurseFullRole[],
		requiredAmountInLocation: number,
		sectionJobId: string | null,
	): PostedMatch | null {
		if (requiredAmountInLocation <= 0) return null;

		const dayStart = dayDate.startOf("day");
		const gapStart = dayStart.plus({ minutes: startMin });
		const gapEnd = dayStart.plus({ minutes: endMin > startMin ? endMin : endMin + MINUTES_PER_DAY /*Add 24HR overnight*/ });

		// Location + job filter. A request with no location falls back to a location wildcard (a
		// dept-wide request matches any cell), mirroring the job fallback where a request with no job_id
		// falls back to role matching; role/role-group sections pass no sectionJobId so are unconstrained.
		// Order candidates located-first so a request naming this location claims its own cell before a
		// dept-wide (wildcard) request consumes that cell's budget. Array.prototype.sort is stable, so
		// activeRequests order is preserved within each group.
		// A request holding budget for exactly this window goes ahead of both: otherwise another request could
		// take the cell its reservation is waiting for, and the reserved budget would surface nowhere.
		const isOwnWindow = (req: OpenShiftRequest): boolean =>
			req.message.shift_start.valueOf() === gapStart.valueOf() && req.message.shift_end.valueOf() === gapEnd.valueOf();
		// DEFAULT: a request with no reservation keeps nothing back.
		const reservedOf = (req: OpenShiftRequest): number => reserved.get(req.id) ?? 0;
		const locatedFirst = (req: OpenShiftRequest): number => (req.location === locationName ? 0 : 1);
		const reservedHereFirst = (req: OpenShiftRequest): number => (reservedOf(req) > BUDGET_EPS && isOwnWindow(req) ? 0 : 1);
		const candidates = activeRequests
			.filter((req) => (!req.location || req.location === locationName) && (!sectionJobId || !req.job_id || req.job_id === sectionJobId))
			.sort((a, b) => reservedHereFirst(a) - reservedHereFirst(b) || locatedFirst(a) - locatedFirst(b));

		// Shared per-candidate body. When `applyThreshold` is set (strict pass) the overlap must clear
		// the half-of-shorter gate; otherwise (loose pass) any positive overlap qualifies. On a commit
		// it mutates the budget, reports the match via `onMatched`, and returns the PostedMatch.
		function tryReq(req: OpenShiftRequest, applyThreshold: boolean): PostedMatch | null {
			const budget = budgets.get(req.id) ?? 0;
			const reqStart = req.message.shift_start;
			const reqEnd = req.message.shift_end;
			const isExactWindow = isOwnWindow(req);
			if (onlyExactWindow && !isExactWindow) return null;
			const reservedBudget = reservedOf(req);
			const usableBudget = isExactWindow ? budget : budget - reservedBudget;
			if (usableBudget <= BUDGET_EPS) return null;

			const overlapStartMs = Math.max(gapStart.valueOf(), reqStart.valueOf());
			const overlapEndMs = Math.min(gapEnd.valueOf(), reqEnd.valueOf());
			const overlapMin = (overlapEndMs - overlapStartMs) / 60_000;
			if (overlapMin <= 0) return null;

			// `postedDurationMin` also feeds the fractional-budget math below, so it is computed in both passes.
			const postedDurationMin = (reqEnd.valueOf() - reqStart.valueOf()) / 60_000;
			const groupDurationMin = endMin > startMin ? endMin - startMin : endMin + MINUTES_PER_DAY - startMin;
			if (applyThreshold) {
				const overlapThresholdMin = MIN_OVERLAP_FRACTION * Math.min(postedDurationMin, groupDurationMin);
				if (overlapMin < overlapThresholdMin - OVERLAP_EPS) return null;
			}

			// Role check applies only when we did NOT match by job. In Jobs view the job filter above is the
			// authoritative dimension and `roles` is the whole department's role set (so this intersection
			// is a near-no-op there); skipping it also lets a correctly job-tagged request through even if
			// its `message.roles` is stale/empty. When there's no job match — role/role-group sections, or a
			// request with no `job_id` falling back to role matching — the role intersection is the real filter.
			const matchedByJob = Boolean(sectionJobId) && Boolean(req.job_id);
			if (!matchedByJob) {
				const reqRoles: Set<NurseFullRole> = new Set(req.message.roles as NurseFullRole[]);
				const rolesMatch = roles.length === 0 || roles.some((r) => reqRoles.has(r));
				if (!rolesMatch) return null;
			}

			// One required slot costs overlapMin / postedDurationMin of budget (∈ (0, 1]).
			const perUnit = postedDurationMin > 0 ? overlapMin / postedDurationMin : 1;
			const maxProvidable = Math.min(requiredAmountInLocation, Math.floor((usableBudget + BUDGET_EPS) / perUnit));
			if (maxProvidable <= 0) return null;

			const consumed = maxProvidable * perUnit;
			budgets.set(req.id, Math.max(0, budget - consumed));
			if (isExactWindow) reserved.set(req.id, Math.max(0, reservedBudget - consumed));
			onMatched(req.id, consumed);
			return {
				requestId: req.id,
				providedAmountAfterMatching: maxProvidable,
				postedStartMin: reqStart.hour * 60 + reqStart.minute,
				postedEndMin: reqEnd.hour * 60 + reqEnd.minute,
			};
		}

		// Sub-loop 1: strict-eligible requests, threshold-gated. First committing match wins.
		for (const req of candidates) {
			if (!isStrict(req.id)) continue;
			const match = tryReq(req, /*applyThreshold*/ true);
			if (match) return match;
		}

		// Sub-loop 2 (only reached if no strict request claimed the cell): loose-eligible requests fill
		// leftover cells with no threshold gate. Iterates the same pre-ordered `candidates` (located-first,
		// then activeRequests order within each group) that the strict pass used.
		for (const req of candidates) {
			if (isStrict(req.id)) continue;
			const match = tryReq(req, /*applyThreshold*/ false);
			if (match) return match;
		}

		return null;
	}

	return { tryMatch };
}

/**
 * Runs `build` once per matching pass and returns the last pass's result — `build` calls `tryMatch` for every missing
 * slot, in the same order each time, and its output from the earlier passes is discarded.
 *
 * 1. Exact-window pass: how much of each request the slots with ITS OWN window can take — the reservation the other two
 *    keep back from every other slot, so an earlier group the post merely overlaps (2:30a–10:30a) cannot claim a 4a–4p
 *    post before the 4a–4p group it was posted for is visited.
 * 2. Strict probe: which requests find a strict (half-overlap-gated) home anywhere. The strict-vs-fallback decision is
 *    per-request AND global — a request falls back to loose (any-overlap) matching only if it found NO strict home
 *    across all sections, which a single per-slot pass cannot know.
 * 3. Final pass: those requests strict, the rest allowed to fall back.
 */
export const buildWithPostedMatches = <T>(staffRequests: ShiftRequest[] | null, build: (tryMatch: TryMatchFn) => T): T => {
	const reservedForExact = new Map<string, number>();
	build(
		createPostedRequestMatcher(staffRequests, {
			onlyExactWindow: true,
			// DEFAULT: a request's first exact match starts its reservation from nothing.
			onMatched: (id, amount) => reservedForExact.set(id, (reservedForExact.get(id) ?? 0) + amount),
		}).tryMatch,
	);

	const strictlyMatched = new Set<string>();
	build(createPostedRequestMatcher(staffRequests, { isStrict: () => true, reservedForExact, onMatched: (id) => strictlyMatched.add(id) }).tryMatch);

	return build(createPostedRequestMatcher(staffRequests, { isStrict: (id) => strictlyMatched.has(id), reservedForExact }).tryMatch);
};
